import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguageLearning } from '../contexts/LanguageLearningContext';
import {
  SELECTABLE_UI_LANGUAGES,
  getUiLanguage,
  normalizeUiLanguageCode,
  setUiLanguageFollowsLearningLanguage,
} from '../constants/uiLanguages';
import {
  buildHomepagePathForUiLanguage,
  isHomepageUiPath,
  getPathBoundUiLanguage,
} from '../lib/uiRouteLanguage';
import { buildLocalizedPath, stripLocalizedRoutePrefix } from '../utils/localizedRoutes';

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const { activeLanguage } = useLanguageLearning();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const languages = SELECTABLE_UI_LANGUAGES.map((language) => ({
    code: language.code,
    name: language.shortName,
    fullName: language.nativeName,
  }));

  const currentLanguageCode = normalizeUiLanguageCode(i18n.language);
  const currentLanguageInfo = getUiLanguage(currentLanguageCode);
  const currentLanguage = languages.find((lang) => lang.code === currentLanguageCode) || {
    code: currentLanguageInfo.code,
    name: currentLanguageInfo.shortName,
    fullName: currentLanguageInfo.nativeName,
  };

  const handleLanguageChange = (languageCode) => {
    const normalizedUiLanguage = normalizeUiLanguageCode(languageCode);
    const normalizedLearningLanguage = normalizeUiLanguageCode(activeLanguage, normalizedUiLanguage);
    setUiLanguageFollowsLearningLanguage(normalizedUiLanguage === normalizedLearningLanguage);

    if (isHomepageUiPath(location.pathname)) {
      void i18n.changeLanguage(normalizedUiLanguage);
      navigate({
        pathname: buildHomepagePathForUiLanguage(normalizedUiLanguage),
        search: location.search,
        hash: location.hash,
      });
      setIsOpen(false);
      return;
    }

    // For language-bound routes (blog, faq, store, etc.) that have /:lang/ equivalents,
    // navigate to the language-prefixed URL so the address bar reflects the language
    // and a page refresh keeps the correct language.
    // We detect these by checking whether the stripped path with a test prefix is route-bound.
    const strippedPath = stripLocalizedRoutePrefix(location.pathname);
    const testPath = buildLocalizedPath(strippedPath, normalizedUiLanguage);
    if (getPathBoundUiLanguage(testPath) !== null) {
      void i18n.changeLanguage(normalizedUiLanguage);
      navigate({
        pathname: testPath,
        search: location.search,
        hash: location.hash,
      });
      setIsOpen(false);
      return;
    }

    i18n.changeLanguage(normalizedUiLanguage);
    setIsOpen(false);
  };

  return (
    <div className="relative"
         onMouseEnter={() => setIsOpen(true)}
         onMouseLeave={() => setIsOpen(false)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-[#B2288E] hover:text-[#9d1c7f] transition-colors duration-200"
        aria-label={`Select language: ${currentLanguage.name}`}
      >
        <span>{currentLanguage.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          <div className="absolute right-0 mt-1 py-2 w-44 bg-white rounded-lg shadow-lg border border-stone-200 z-20">
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-stone-50 transition-colors duration-150 ${
                  language.code === currentLanguage.code
                    ? 'text-[#B2288E] bg-purple-50'
                    : 'text-stone-700'
                }`}
              >
                <div className="grid grid-cols-[2rem_minmax(0,1fr)] items-center gap-3">
                  <span className="font-medium">{language.name}</span>
                  <span className="text-left text-xs leading-tight text-stone-500">{language.fullName}</span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
